/**
 * Утилиты для нормализации и сравнения selections комбо-товаров.
 * Centralizes logic that was duplicated across comboInCart, comboQuantity, handleComboQuantityChange.
 */

import type { CartComboItem, CartItem } from '@/utils/types/cart';

/** Добавки/отбавки, выбранные для конкретного товара внутри слота комбо */
export interface ComboSelectionModifiers {
  product_id: number;
  addons?: number[];
  excludes?: number[];
}

export interface NormalizedSelection {
  group_id: string;
  product_ids: number[];
  /** Есть только если пользователь менял состав слота */
  modifiers?: Array<{ product_id: number; addons: number[]; excludes: number[] }>;
}

/** Каноничный вид модификаторов: пустые выброшены, ID отсортированы */
function normalizeModifiers(raw: unknown): NormalizedSelection['modifiers'] {
  if (!Array.isArray(raw)) return undefined;

  const modifiers = raw
    .filter((m): m is Record<string, any> => !!m && typeof m === 'object')
    .map((m) => ({
      product_id: Number(m.product_id ?? m.productId),
      addons: Array.isArray(m.addons) ? m.addons.map(Number).sort((a, b) => a - b) : [],
      excludes: Array.isArray(m.excludes) ? m.excludes.map(Number).sort((a, b) => a - b) : [],
    }))
    .filter((m) => Number.isFinite(m.product_id) && (m.addons.length > 0 || m.excludes.length > 0))
    .sort((a, b) => a.product_id - b.product_id);

  return modifiers.length > 0 ? modifiers : undefined;
}

/**
 * Приводит selections из любого формата (массив с сервера или объект {groupId: productId})
 * к единому нормализованному массиву, отсортированному по group_id.
 *
 * Модификаторы слотов («Изменить состав») входят в нормализованный вид: комбо с разными
 * добавками — разные позиции корзины, как и на бэкенде.
 */
export function normalizeSelections(selections: unknown): NormalizedSelection[] | null {
  if (!selections) return null;

  if (Array.isArray(selections)) {
    return (selections as any[])
      .map((s) => {
        const normalized: NormalizedSelection = {
          group_id: String(s.group_id ?? s.groupId),
          product_ids: Array.isArray(s.product_ids ?? s.productIds)
            ? (s.product_ids ?? s.productIds).map(Number)
            : [Number(s.product_id ?? s.productId ?? s.product_ids ?? s.productIds)],
        };

        const modifiers = normalizeModifiers(s.modifiers);
        if (modifiers) normalized.modifiers = modifiers;

        return normalized;
      })
      .sort((a, b) => Number(a.group_id) - Number(b.group_id));
  }

  if (typeof selections === 'object' && selections !== null) {
    return Object.entries(selections as Record<string, unknown>)
      .map(([groupId, productId]) => ({
        group_id: String(groupId),
        product_ids: [Number(productId)],
      }))
      .sort((a, b) => Number(a.group_id) - Number(b.group_id));
  }

  return null;
}

/**
 * Уникальный ID позиции комбо в корзине: `combo-{id}-{base64(selections)}`.
 * Единая точка — иначе позиции, добавленные локально и пришедшие с сервера, не совпадут
 * (а с модификаторами слотов ещё и склеятся две разные позиции).
 */
export function buildComboUniqueId(comboId: string | number, selections: unknown): string {
  const normalized = normalizeSelections(selections);
  if (!normalized || normalized.length === 0) return `combo-${comboId}`;
  return `combo-${comboId}-${btoa(JSON.stringify(normalized))}`;
}

/**
 * Сравнивает два набора selections (любой формат), возвращает true если они идентичны.
 */
export function selectionsMatch(a: unknown, b: unknown): boolean {
  const normA = normalizeSelections(a);
  const normB = normalizeSelections(b);
  if (!normA || !normB) return false;
  return JSON.stringify(normA) === JSON.stringify(normB);
}

/**
 * Находит элемент комбо в корзине по comboId, branchId и selections.
 * Единая точка поиска — устраняет дублирование в comboInCart / comboQuantity / handleChange.
 */
export function findComboCartItem(
  cartItems: CartItem[],
  comboId: number | string,
  branchId: number | string,
  selections: unknown,
): CartComboItem | undefined {
  return cartItems.find((item): item is CartComboItem => {
    if (item.itemType !== 'combo') return false;
    if (String((item as CartComboItem).comboId) !== String(comboId)) return false;
    if (String(item.branch_id) !== String(branchId)) return false;
    return selectionsMatch((item as CartComboItem).comboSelections, selections);
  });
}
